import { useState } from 'react';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [count, setCount] = useState(0);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);

    const honey = String(data.get('contact_alt') ?? '').trim();
    if (honey) {
      setStatus('success');
      return;
    }

    const message = String(data.get('message') ?? '');
    if (message.length > 500) {
      setFormError('反馈内容不能超过 500 个字符。');
      setStatus('error');
      return;
    }

    data.delete('contact_alt');
    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch('https://readdy.ai/api/form/dbad59kdsci36477l6mg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
        setCount(0);
      } else {
        setFormError(
          serverMsg && !isSpam
            ? serverMsg
            : '提交没有成功，请稍后再试一次。',
        );
        setStatus('error');
      }
    } catch {
      setFormError('网络连接出现问题，请检查网络后重试。');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="rounded-lg bg-background-50 border border-background-200 p-6 md:p-8 flex flex-col items-center text-center">
        <span className="w-14 h-14 rounded-full bg-primary-50 text-primary-600 flex items-center justify-center mb-4">
          <i className="ri-checkbox-circle-line text-2xl"></i>
        </span>
        <h3 className="font-heading text-lg font-bold text-foreground-950">感谢你的反馈</h3>
        <p className="mt-2 text-sm text-foreground-600 max-w-sm leading-relaxed">
          我们已经收到你的留言，会作为内容与体验改进的参考。
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-5 px-5 h-10 rounded-md border border-background-300 text-sm text-foreground-800 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
        >
          再写一条
        </button>
      </div>
    );
  }

  return (
    <form
      id="sigua-feedback-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-6 space-y-4"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="fb-name" className="block text-xs font-medium text-foreground-700 mb-1.5">
            你的称呼
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            required
            maxLength={40}
            placeholder="怎么称呼你"
            className="w-full h-10 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400"
          />
        </div>
        <div>
          <label htmlFor="fb-email" className="block text-xs font-medium text-foreground-700 mb-1.5">
            联系邮箱
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="name@example.com"
            className="w-full h-10 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400"
          />
        </div>
      </div>

      <div>
        <label htmlFor="fb-type" className="block text-xs font-medium text-foreground-700 mb-1.5">
          反馈类型
        </label>
        <select
          id="fb-type"
          name="feedback_type"
          defaultValue="内容建议"
          className="w-full h-10 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-950 outline-none focus:border-primary-400 cursor-pointer"
        >
          <option value="内容建议">内容建议</option>
          <option value="页面体验">页面体验</option>
          <option value="功能问题">功能问题</option>
          <option value="合作咨询">合作咨询</option>
        </select>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label htmlFor="fb-message" className="block text-xs font-medium text-foreground-700">
            反馈内容
          </label>
          <span className="text-[11px] text-foreground-400 font-label">{count} / 500</span>
        </div>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          onChange={(event) => setCount(event.target.value.length)}
          placeholder="想看的类型、希望改进的地方，都可以告诉我们"
          className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-950 placeholder:text-foreground-400 outline-none focus:border-primary-400 resize-none"
        ></textarea>
      </div>

      <div className="form-honey" aria-hidden="true">
        <label htmlFor="fb-contact-alt">请勿填写此项</label>
        <input
          id="fb-contact-alt"
          name="contact_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      {status === 'error' && formError && (
        <p className="text-xs text-primary-700 bg-primary-50 border border-primary-200 rounded-md px-3 py-2 leading-relaxed">
          {formError}
        </p>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-1">
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition-colors whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
        >
          {status === 'submitting' ? '提交中…' : '提交反馈'}
        </button>
        <p className="text-[11px] text-foreground-500 leading-relaxed">
          反馈仅用于平台内容与体验改进，不会用于其他用途。
        </p>
      </div>
    </form>
  );
}